/**
 * Note DSL Parser
 *
 * Parses compact note notation into Note objects.
 *
 * Format: beat:pitch/duration velocity%
 *
 * Examples:
 *   0:C4           → C4 at beat 0, duration 1, velocity 100
 *   0:C4/0.5       → C4 at beat 0, half-beat duration
 *   0:C4/1 80%     → C4 with velocity 80
 *   0.5:D#4/0.25   → 16th note on the off-beat
 *   0:C4,E4,G4     → C major chord at beat 0
 */

import { Note, createNote, asBeats } from './types.js';

// =============================================================================
// Pitch Parsing
// =============================================================================

/**
 * Note name to semitone offset from C.
 */
const NOTE_NAMES: Record<string, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

/**
 * Parse a pitch string like "C4", "F#3", "Bb5" to MIDI note number.
 *
 * @param pitch - Pitch string (e.g., "C4", "F#3", "Bb5")
 * @returns MIDI note number (0-127)
 * @throws Error if pitch is invalid
 */
export function parsePitch(pitch: string): number {
  // Match: note name + optional accidental + octave
  const match = pitch.match(/^([A-Ga-g])([#b]?)(-?\d+)$/);
  if (!match) {
    throw new Error(`Invalid pitch: "${pitch}"`);
  }

  const [, noteName, accidental, octaveStr] = match;
  const baseNote = NOTE_NAMES[noteName.toUpperCase()];
  if (baseNote === undefined) {
    throw new Error(`Invalid note name: "${noteName}"`);
  }

  const octave = parseInt(octaveStr, 10);
  let midiNote = (octave + 1) * 12 + baseNote;

  // Apply accidental
  if (accidental === '#') {
    midiNote += 1;
  } else if (accidental === 'b') {
    midiNote -= 1;
  }

  // Validate range
  if (midiNote < 0 || midiNote > 127) {
    throw new Error(`Pitch out of MIDI range: "${pitch}" (${midiNote})`);
  }

  return midiNote;
}

/**
 * Convert MIDI note number to pitch string.
 *
 * @param midiNote - MIDI note number (0-127)
 * @param preferSharps - Use sharps instead of flats (default true)
 * @returns Pitch string (e.g., "C4", "F#3")
 */
export function pitchToString(midiNote: number, preferSharps: boolean = true): string {
  const octave = Math.floor(midiNote / 12) - 1;
  const semitone = midiNote % 12;

  const noteNames = preferSharps
    ? ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
    : ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

  return `${noteNames[semitone]}${octave}`;
}

// =============================================================================
// Single Note Parsing
// =============================================================================

/**
 * Parse a single note token.
 *
 * Format: beat:pitch/duration velocity%
 * - beat: required (number, can be fractional)
 * - pitch: required (C4, F#3, Bb5)
 * - duration: optional, default 1
 * - velocity: optional, default 100
 *
 * @param token - Single note token (e.g., "0:C4/0.5 80%")
 * @returns Parsed Note
 * @throws Error if token is invalid
 */
export function parseNoteToken(token: string): Note {
  // Trim whitespace
  token = token.trim();
  if (!token) {
    throw new Error('Empty note token');
  }

  // Split off velocity suffix if present
  let velocity = 100;
  const velocityMatch = token.match(/\s+(\d+)%$/);
  if (velocityMatch) {
    velocity = parseInt(velocityMatch[1], 10);
    if (velocity < 0 || velocity > 127) {
      throw new Error(`Velocity out of range: ${velocity}`);
    }
    token = token.slice(0, -velocityMatch[0].length);
  }

  // Split time:pitch/duration
  const colonIdx = token.indexOf(':');
  if (colonIdx === -1) {
    throw new Error(`Missing time separator ':' in note: "${token}"`);
  }

  const timeStr = token.slice(0, colonIdx);
  const time = parseFloat(timeStr);
  if (isNaN(time) || time < 0) {
    throw new Error(`Invalid time: "${timeStr}"`);
  }

  // Parse pitch and optional duration
  let pitchAndDuration = token.slice(colonIdx + 1);
  let duration = 1;

  const slashIdx = pitchAndDuration.indexOf('/');
  let pitchStr: string;
  if (slashIdx !== -1) {
    pitchStr = pitchAndDuration.slice(0, slashIdx);
    const durationStr = pitchAndDuration.slice(slashIdx + 1);
    duration = parseFloat(durationStr);
    if (isNaN(duration) || duration <= 0) {
      throw new Error(`Invalid duration: "${durationStr}"`);
    }
  } else {
    pitchStr = pitchAndDuration;
  }

  const pitch = parsePitch(pitchStr);

  return createNote(pitch, time, duration, velocity);
}

// =============================================================================
// Multi-Note Parsing (Chords)
// =============================================================================

/**
 * Parse a note token that may contain chord shorthand.
 *
 * Chord format: beat:pitch1,pitch2,pitch3/duration velocity%
 * All notes in the chord share the same time, duration, and velocity.
 *
 * @param token - Note token possibly containing comma-separated pitches
 * @returns Array of Notes (1 for single note, multiple for chord)
 */
export function parseNoteTokenWithChords(token: string): Note[] {
  token = token.trim();
  if (!token) {
    throw new Error('Empty note token');
  }

  // Split off velocity suffix if present
  let velocity = 100;
  const velocityMatch = token.match(/\s+(\d+)%$/);
  if (velocityMatch) {
    velocity = parseInt(velocityMatch[1], 10);
    if (velocity < 0 || velocity > 127) {
      throw new Error(`Velocity out of range: ${velocity}`);
    }
    token = token.slice(0, -velocityMatch[0].length);
  }

  // Split time:pitches/duration
  const colonIdx = token.indexOf(':');
  if (colonIdx === -1) {
    throw new Error(`Missing time separator ':' in note: "${token}"`);
  }

  const timeStr = token.slice(0, colonIdx);
  const time = parseFloat(timeStr);
  if (isNaN(time) || time < 0) {
    throw new Error(`Invalid time: "${timeStr}"`);
  }

  // Parse pitches and optional duration
  let pitchesAndDuration = token.slice(colonIdx + 1);
  let duration = 1;

  const slashIdx = pitchesAndDuration.indexOf('/');
  let pitchesStr: string;
  if (slashIdx !== -1) {
    pitchesStr = pitchesAndDuration.slice(0, slashIdx);
    const durationStr = pitchesAndDuration.slice(slashIdx + 1);
    duration = parseFloat(durationStr);
    if (isNaN(duration) || duration <= 0) {
      throw new Error(`Invalid duration: "${durationStr}"`);
    }
  } else {
    pitchesStr = pitchesAndDuration;
  }

  // Split by comma for chords
  const pitchStrings = pitchesStr.split(',').map((s) => s.trim());
  if (pitchStrings.length === 0 || pitchStrings.some((s) => !s)) {
    throw new Error(`Invalid pitch list: "${pitchesStr}"`);
  }

  return pitchStrings.map((pitchStr) => {
    const pitch = parsePitch(pitchStr);
    return createNote(pitch, time, duration, velocity);
  });
}

// =============================================================================
// Line / Block Parsing
// =============================================================================

/**
 * Parse a line containing one or more space-separated note tokens.
 *
 * @param line - Line of note tokens (e.g., "0:C4 1:E4 2:G4")
 * @returns Array of Notes
 */
export function parseNoteLine(line: string): Note[] {
  // Remove comments (using ; to avoid conflict with sharps like C#4)
  const commentIdx = line.indexOf(';');
  if (commentIdx !== -1) {
    line = line.slice(0, commentIdx);
  }

  line = line.trim();
  if (!line) {
    return [];
  }

  // Split by whitespace, but be careful with velocity suffix
  // Tokens are separated by whitespace, but "0:C4 80%" is one token
  const notes: Note[] = [];
  const tokens = tokenizeLine(line);

  for (const token of tokens) {
    const parsed = parseNoteTokenWithChords(token);
    notes.push(...parsed);
  }

  return notes;
}

/**
 * Tokenize a line into note tokens, handling velocity suffixes.
 *
 * The tricky part: "0:C4 80%" should be one token, not two.
 * We detect this by checking if the next part is just "NN%".
 */
function tokenizeLine(line: string): string[] {
  const parts = line.split(/\s+/);
  const tokens: string[] = [];

  let i = 0;
  while (i < parts.length) {
    let token = parts[i];

    // Check if next part is a velocity suffix (just "NN%")
    if (i + 1 < parts.length && /^\d+%$/.test(parts[i + 1])) {
      token += ' ' + parts[i + 1];
      i += 2;
    } else {
      i += 1;
    }

    tokens.push(token);
  }

  return tokens;
}

/**
 * Parse a multi-line note block (e.g., from YAML |).
 *
 * @param block - Multi-line string of notes
 * @returns Array of Notes, sorted by time
 */
export function parseNoteBlock(block: string): Note[] {
  const lines = block.split('\n');
  const notes: Note[] = [];

  for (const line of lines) {
    const lineNotes = parseNoteLine(line);
    notes.push(...lineNotes);
  }

  // Sort by time
  notes.sort((a, b) => asBeats(a.time) - asBeats(b.time));

  return notes;
}

// =============================================================================
// Serialization (Note → String)
// =============================================================================

/**
 * Serialize a note back to DSL format.
 *
 * @param note - Note to serialize
 * @param compact - Omit defaults (duration 1, velocity 100)
 * @returns Note string (e.g., "0:C4/0.5 80%")
 */
export function serializeNote(note: Note, compact: boolean = true): string {
  const pitchStr = pitchToString(note.pitch);
  let result = `${asBeats(note.time)}:${pitchStr}`;

  // Add duration if not default or if we need velocity
  const durationNum = asBeats(note.duration);
  const needsDuration = !compact || durationNum !== 1;
  const needsVelocity = !compact || note.velocity !== 100;

  if (needsDuration || needsVelocity) {
    result += `/${durationNum}`;
  }

  if (needsVelocity) {
    result += ` ${note.velocity}%`;
  }

  return result;
}

/**
 * Serialize multiple notes to a block string.
 *
 * @param notes - Notes to serialize
 * @param notesPerLine - How many notes per line (0 = all on one line)
 * @returns Multi-line note block
 */
export function serializeNotes(notes: readonly Note[], notesPerLine: number = 4): string {
  if (notes.length === 0) {
    return '';
  }

  // Sort by time
  const sorted = [...notes].sort((a, b) => asBeats(a.time) - asBeats(b.time));
  const serialized = sorted.map((n) => serializeNote(n));

  if (notesPerLine === 0) {
    return serialized.join(' ');
  }

  // Group into lines
  const lines: string[] = [];
  for (let i = 0; i < serialized.length; i += notesPerLine) {
    lines.push(serialized.slice(i, i + notesPerLine).join(' '));
  }

  return lines.join('\n');
}
